import type { ReactNode } from 'react';
import { Bars, Card, CardHeader, CardTitle, type Bar } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { CampaignRow, Campaigns } from '../types';
import { UNLINKED_PARTNER, campaignWord, partnerWord } from '../funnel/funnel-words';
import '../admin.css';

export interface CampaignsBoardProps {
  campaigns: Campaigns;
  /** The router's link, so every bar opens its cohort in the funnel. */
  renderLink: (href: string, children: ReactNode) => ReactNode;
}

function funnelHref(row: CampaignRow, partner: string | null): string {
  const q = new URLSearchParams({ source: row.source, campaign: row.campaign });
  if (partner) q.set('partner', partner);
  return `/admin/funnel?${q.toString()}`;
}

function barsOf(
  rows: CampaignRow[],
  partner: string | null,
  value: (r: CampaignRow) => number,
  text: (r: CampaignRow) => string,
): Bar[] {
  return rows.map((r) => ({
    label: campaignWord(r.source, r.campaign),
    value: value(r),
    valueText: text(r),
    href: funnelHref(r, partner),
  }));
}

interface Panel {
  title: string;
  sentence: string;
  value: (r: CampaignRow) => number;
  text: (f: Formatter, r: CampaignRow) => string;
}

function costWord(f: Formatter, r: CampaignRow): string {
  if (r.costPerPaid !== null) return f.moneyWhole(r.costPerPaid);
  return r.spend > 0 ? 'nobody paid yet' : 'no spend entered';
}

const panels: readonly Panel[] = [
  {
    title: 'Signed up',
    sentence: 'companies from each campaign',
    value: (r) => r.signedUp,
    text: (f, r) => f.int(r.signedUp),
  },
  {
    title: 'Paid',
    sentence: 'of them on a paid plan',
    value: (r) => r.paid,
    text: (f, r) => f.int(r.paid),
  },
  {
    title: 'List price a month',
    sentence: 'their plans at list price',
    value: (r) => r.monthlyRevenue,
    text: (f, r) => f.moneyWhole(r.monthlyRevenue),
  },
  {
    title: 'Cost of one paying customer',
    sentence: 'what was spent over who paid; enter spend below to fill this in',
    value: (r) => r.costPerPaid ?? 0,
    text: costWord,
  },
];

/**
 * "Partner, unlinked": the period's companies a partner sent that no partner is linked to, so no
 * partner is credited for them (round 3, marketing should 2); the count opens them in the funnel.
 */
function UnlinkedLine({ campaigns, renderLink }: CampaignsBoardProps): ReactNode {
  const n = campaigns.unlinked;
  const words = `${n} ${n === 1 ? 'company' : 'companies'}`;
  const href = `/admin/funnel?${new URLSearchParams({ partner: UNLINKED_PARTNER }).toString()}`;
  return (
    <p className="ad-small ad-campaigns__unlinked">
      {partnerWord(UNLINKED_PARTNER)}: {n > 0 ? renderLink(href, words) : 'none'}
      {n > 0
        ? ` came from a partner and ${n === 1 ? 'has' : 'have'} no partner linked, so no partner is credited.`
        : '.'}
    </p>
  );
}

/**
 * Four bar charts over the same campaigns, paid first; every bar opens that cohort in the funnel.
 * Narrowed to a partner, the cost panel goes: spend is bought by campaign, not by partner.
 */
export function CampaignsBoard({ campaigns, renderLink }: CampaignsBoardProps): ReactNode {
  const f = useFormat();
  const rows = campaigns.campaigns;
  if (rows.length === 0) {
    return (
      <Card>
        <p className="ad-small">
          Nobody signed up in this period, so there is nothing to compare yet.
        </p>
      </Card>
    );
  }
  const shown = campaigns.partner ? panels.slice(0, 3) : panels;
  return (
    <div className="ad-campaigns">
      <UnlinkedLine campaigns={campaigns} renderLink={renderLink} />
      {shown.map((p) => (
        <Card key={p.title} className="ad-campaigns__panel">
          <CardHeader>
            <CardTitle>{p.title}</CardTitle>
          </CardHeader>
          <span className="ad-small">{p.sentence}</span>
          <Bars
            bars={barsOf(rows, campaigns.partner, p.value, (r) => p.text(f, r))}
            label={`${p.title} by campaign`}
            renderLink={renderLink}
            format={f.int}
            width={520}
          />
        </Card>
      ))}
    </div>
  );
}
